// frontend/src/pages/ContactsPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-11
// Updated:      2026-09-25 10:45 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:  Contacts page modeled after DashboardPage. Uses grid layout,
//               with a dedicated mobile presentation for responsive page flow.
//
//               default CRM widgets, category filtering. Now also wires
//               contact-scoped messaging so channel buttons and history rows
//               in ContactMessagesAccordian open the appropriate message modals.
//               • New WhatsApp button now opens WhatsAppTemplateSelectorModal
//                 for business-initiated template messages.
//               • Thread clicks still open WhatsAppMessageModal with history.
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated mobile presentation at <= 768px.
//   - Mobile renders configured Contact Manager widgets in a natural single-column flow.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - react-grid-layout drag/resize behaviour remains desktop-only.
//   - Mobile rendering never persists layout changes, so viewing Contacts on a phone
//     cannot alter the user's desktop widget arrangement.
//   - Reflowed the page heading and Add Widget action for compact mobile widths.
//   - Reduced mobile page gutters while preserving contact-scoped email/SMS/WhatsApp
//     workflows, template sending, localStorage keys, widget registration, and desktop layout.
//
// Previous FIX (2025-12-22):
//   Template sends were still showing "(template message)" because this file
//   ignored payload.body_text from WhatsAppTemplateSelectorModal and attempted
//   to build preview text from payload.raw_template.db.body_text (often empty).
//   Now we:
//     1) Prefer payload.body_text (authoritative rendered preview from selector)
//     2) Still support fallback rendering from raw template preview/body if needed
//     3) Persist template_preview_text in metadata_json (same as WhatsAppMessageModal)

import React, { useState, useEffect, useCallback, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import ManageUserModal from "../components/ManageUserModal";
import EditCustomerModal from "../components/EditCustomerModal";
import EditContactModal from "../components/EditContactModal";

// ✉️ Messaging modals (same components used from Job side)
import EmailMessageModal from "../components/EmailMessageModal";
import SmsMessageModal from "../components/SmsMessageModal";
import WhatsAppMessageModal from "../components/WhatsAppMessageModal";
import WhatsAppTemplateSelectorModal from "../components/WhatsAppTemplateSelectorModal";

import AdvancedContactListWidget from "../widgets/AdvancedContactListWidget.jsx";
import CustomerFormWidget from "../widgets/CustomerFormWidget.jsx";
import AdvancedCustomerListWidget from "../widgets/AdvancedCustomerListWidget.jsx";
import ContactFormWidget from "../widgets/ContactFormWidget.jsx";
import JobListWidget from "../widgets/JobListWidget.jsx";

import { markWidgetUsedIn } from "../widgets/WidgetRegistry";
import { useTheme } from "../context/ThemeContext.jsx";

// ✅ Use your central axios instance for backend calls
import api from "../api/axiosInstance";

const STORAGE_KEY_LAYOUT = "contactsLayout";
const STORAGE_KEY_WIDGETS = "contactsWidgets";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const apply = () => {
      setIsMobile(mediaQuery.matches);
    };

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultLayout = [
  { i: "advanced_contact_list", x: 0, y: 0, w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
  { i: "contact_form", x: 6, y: 0, w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
  { i: "job_list", x: 0, y: 5, w: 12, h: 5, minW: 4, minH: 4, maxW: 12 },
];

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const ContactsPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [gridWidth, setGridWidth] = useState(1200);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  // 💬 contact-scoped messaging modal state
  const [emailModalCfg, setEmailModalCfg] = useState(null);
  const [smsModalCfg, setSmsModalCfg] = useState(null);
  const [whatsAppModalCfg, setWhatsAppModalCfg] = useState(null);
  const [whatsAppTemplateCfg, setWhatsAppTemplateCfg] = useState(null);

  const gridRef = useRef(null);

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;

    const updateWidth = () => {
      setGridWidth(el.offsetWidth || 1200);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // ─────────────────────────────────────────────
  // Handlers for ContactMessagesAccordian
  // ─────────────────────────────────────────────

  const handleContactEmail = (payload) => {
    const mode = payload?.mode || "new";
    const contactId = payload?.contactId ?? null;
    const conversationId = payload?.conversationId ?? null;
    const messageId = payload?.messageId ?? null;

    console.info("[ContactsPage] handleContactEmail", {
      contactId,
      conversationId,
      messageId,
      mode,
    });

    setEmailModalCfg({
      contactId,
      conversationId,
      messageId,
      mode,
      channel: "email",
      source: "contacts",
    });
  };

  const handleContactSms = (payload) => {
    const mode = payload?.mode || "new";
    const contactId = payload?.contactId ?? null;
    const conversationId = payload?.conversationId ?? null;
    const messageId = payload?.messageId ?? null;

    console.info("[ContactsPage] handleContactSms", {
      contactId,
      conversationId,
      messageId,
      mode,
    });

    setSmsModalCfg({
      contactId,
      conversationId,
      messageId,
      mode,
      channel: "sms",
      source: "contacts",
    });
  };

  const handleContactWhatsApp = (payload) => {
    const mode = payload?.mode || "new";
    const contactId = payload?.contactId ?? null;
    const conversationId = payload?.conversationId ?? null;
    const messageId = payload?.messageId ?? null;

    console.info("[ContactsPage] handleContactWhatsApp", {
      contactId,
      conversationId,
      messageId,
      mode,
    });

    if (mode === "thread") {
      // Existing conversation → open chat modal with history (same as Jobs side)
      setWhatsAppModalCfg({
        contactId,
        conversationId,
        messageId,
        mode,
        channel: "whatsapp",
        source: "contacts",
      });
    } else {
      // New business-initiated → open template selector
      setWhatsAppTemplateCfg({
        contactId,
        mode,
        // contactName / phone can be threaded in later from the widget if needed
      });
    }
  };

  // ─────────────────────────────────────────────
  // ✅ Send WhatsApp template via backend
  // ─────────────────────────────────────────────

  const normaliseUkToE164 = (raw) => {
    const s = String(raw || "").trim().replace(/\s+/g, "");
    if (!s) return "";
    if (s.startsWith("+")) return s;
    if (s.startsWith("0")) return `+44${s.slice(1)}`;
    if (s.startsWith("44")) return `+${s}`;
    return s;
  };

  const toWhatsAppAddress = (rawPhone) => {
    const e164 = normaliseUkToE164(rawPhone);
    if (!e164) return "";
    return e164.startsWith("whatsapp:") ? e164 : `whatsapp:${e164}`;
  };

  const renderTemplateText = (text, placeholders) => {
    let out = String(text || "");
    Object.keys(placeholders || {}).forEach((k) => {
      const v = String(placeholders[k] ?? "");
      out = out.replaceAll(`{{${k}}}`, v);
      out = out.replaceAll(`{${k}}`, v);
    });
    return out;
  };

  const pickTemplateBaseText = (payload) => {
    // Best-effort base text for rendering if payload.body_text is missing.
    // Supports both selector's raw_template shape and older shapes.
    const rt = payload?.raw_template;

    // If selector merged db+provider, raw_template is often: { db: {...}, provider: {...} }
    const db = rt?.db || rt?.raw?.db || null;
    const provider = rt?.provider || rt?.raw?.provider || null;

    const candidates = [
      payload?.template_preview_text,
      db?.body_preview,
      db?.body_text,
      db?.preview,
      db?.body,
      provider?.preview,
      provider?.body_text,
      provider?.body_preview,
      provider?.body,
    ];

    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c.trim();
    }
    return "";
  };

  const sendWhatsAppTemplateMessage = async (payload) => {
    const step = { name: "init" };

    try {
      const tenantId = payload?.tenant_id ?? 1;
      const contactId = payload?.contact_id ?? payload?.contactId ?? null;

      const to = toWhatsAppAddress(payload?.contact_phone ?? payload?.to ?? "");
      const templateContentSid =
        payload?.template_content_sid ||
        payload?.content_sid ||
        payload?.template_id ||
        payload?.templateId ||
        "";

      const placeholders = payload?.placeholders || payload?.content_variables || {};

      // ✅ FIX: Use selector-rendered text first (authoritative), then fallback to best-effort render.
      const selectorRendered = typeof payload?.body_text === "string" ? payload.body_text.trim() : "";
      const baseText = pickTemplateBaseText(payload);
      const fallbackRendered = baseText ? renderTemplateText(baseText, placeholders) : "";

      const previewText =
        selectorRendered ||
        fallbackRendered ||
        "(template message)";

      if (!contactId) throw new Error("Missing contact_id for WhatsApp template send");
      if (!to) throw new Error("Missing/invalid contact_phone for WhatsApp template send");
      if (!templateContentSid) throw new Error("Missing template_content_sid for WhatsApp send");

      // 1) Create conversation
      step.name = "create_conversation";
      const convPayload = {
        tenant_id: tenantId,
        channel: "whatsapp",
        contact_id: contactId,
        job_id: payload?.job_id ?? null,
        subject: payload?.template_friendly_name || payload?.template_name || "WhatsApp Template",
      };

      console.info("[ContactsPage] create_conversation payload", convPayload);
      const convRes = await api.post("/api/conversations/", convPayload);

      const conversationId =
        convRes?.data?.id ||
        convRes?.data?.conversation_id ||
        convRes?.data?.conversation?.id ||
        null;

      if (!conversationId) throw new Error("Backend did not return a conversation id");

      // 2) Send via conversation send endpoint
      step.name = "send_message";

      // ✅ IMPORTANT: your backend MessageCreate requires conversation_id in BODY (even though it’s also in the URL)
      // ✅ IMPORTANT: send template info via metadata_json so backend can detect & use Content API
      const sendPayload = {
        conversation_id: conversationId,
        tenant_id: tenantId,
        channel: "whatsapp",
        to,
        subject: payload?.template_friendly_name || payload?.template_name || "WhatsApp Template",

        // ✅ FIX: store non-empty preview so UI bubbles never show "(template message)"
        body_text: previewText,

        metadata_json: {
          // Template send (Content API)
          template_content_sid: templateContentSid,
          placeholders,

          // ✅ FIX: persist preview text for the backend / thread rendering consistency
          template_preview_text: previewText,

          // Useful trace/debug
          template_db_id: payload?.template_db_id ?? null,
          template_name: payload?.template_name ?? null,
          template_friendly_name: payload?.template_friendly_name ?? null,
          job_id: payload?.job_id ?? null,
          job_ref: payload?.job_ref ?? "",
          source: payload?.source ?? "template_selector",
          to_whatsapp_normalised: to,
        },
      };

      console.info("[ContactsPage] send_message payload", {
        conversationId,
        ...sendPayload,
      });

      await api.post(`/api/conversations/${conversationId}/send`, sendPayload);

      console.info("[ContactsPage] WhatsApp template send OK", {
        conversationId,
        contactId,
        to,
      });

      setWhatsAppTemplateCfg(null);

      setWhatsAppModalCfg({
        contactId,
        conversationId,
        messageId: null,
        mode: "thread",
        channel: "whatsapp",
        source: "contacts",
      });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      console.error("[ContactsPage] WhatsApp template send FAILED", {
        step: step.name,
        status,
        data,
        err,
      });

      const msg =
        data?.detail ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        "Failed to send WhatsApp template message";

      alert(typeof msg === "string" ? msg : JSON.stringify(msg, null, 2));
    }
  };

  // ─────────────────────────────────────────────
  // Layout / widget bootstrap
  // ─────────────────────────────────────────────

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetIds = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      // ✅ Corruption check
      if (Array.isArray(storedWidgetIds) && storedWidgetIds.some((w) => typeof w !== "string")) {
        console.warn("Corrupted widget storage detected for ContactsPage. Clearing...");
        localStorage.removeItem(STORAGE_KEY_LAYOUT);
        localStorage.removeItem(STORAGE_KEY_WIDGETS);
        throw new Error("Corrupted widget storage");
      }

      if (Array.isArray(storedLayout) && Array.isArray(storedWidgetIds)) {
        const normalised = normaliseLayout(storedLayout);
        setLayout(normalised);

        const components = {};
        storedWidgetIds.forEach((id) => {
          components[id] = resolveWidgetComponent(id);
          markWidgetUsedIn(id, "contacts");
        });

        setWidgets(components);
        return;
      }
    } catch {
      console.warn("Using default layout for ContactsPage");
    }

    // ✅ Default layout if no valid storage
    setLayout(defaultLayout);
    setWidgets({
      advanced_contact_list: (
        <AdvancedContactListWidget
          onEdit={setEditingContact}
          onNewEmail={handleContactEmail}
          onNewSms={handleContactSms}
          onNewWhatsApp={handleContactWhatsApp}
        />
      ),
      contact_form: <ContactFormWidget />,
      job_list: <JobListWidget />,
    });
    markWidgetUsedIn("advanced_contact_list", "contacts");
    markWidgetUsedIn("contact_form", "contacts");
    markWidgetUsedIn("job_list", "contacts");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveWidgetComponent = (id) => {
    switch (id) {
      case "advanced_contact_list":
        return (
          <AdvancedContactListWidget
            onEdit={setEditingContact}
            onNewEmail={handleContactEmail}
            onNewSms={handleContactSms}
            onNewWhatsApp={handleContactWhatsApp}
          />
        );
      case "customer_form":
        return <CustomerFormWidget />;
      case "advanced_customer_list":
        return <AdvancedCustomerListWidget onEditCustomer={setEditingCustomer} />;
      case "contact_form":
        return <ContactFormWidget />;
      case "job_list":
        return <JobListWidget />;
      default:
        return <div style={{ padding: "1rem", color: "red" }}>Unknown widget: {id}</div>;
    }
  };

  const handleAddWidget = (widgetId) => {
    if (widgets[widgetId]) return alert("Widget already exists");
    const component = resolveWidgetComponent(widgetId);
    if (!component) return alert("Unknown widget");

    setWidgets((prev) => ({ ...prev, [widgetId]: component }));
    markWidgetUsedIn(widgetId, "contacts");

    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newLayout = [
        ...prev,
        { i: widgetId, x: 0, y: bottomY, w: 6, h: 4, minW: 4, minH: 3 },
      ];
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify([...Object.keys(widgets), widgetId]));
      return newLayout;
    });
    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const clone = { ...prev };
      delete clone[widgetId];
      return clone;
    });
    setLayout((prev) => {
      const filtered = prev.filter((item) => item.i !== widgetId);
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
      localStorage.setItem(
        STORAGE_KEY_WIDGETS,
        JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId))
      );
      return filtered;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
    },
    [widgets]
  );

  const getWidgetTitle = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  /*
   * Mobile is a presentation of the existing Contact Manager configuration,
   * not a second editable layout. Follow the saved desktop visual order
   * without persisting mobile positions back into contactsLayout.
   */
  const mobileWidgetEntries = Object.entries(widgets).sort(([a], [b]) => {
    const aIndex = layout.findIndex((item) => item.i === a);
    const bIndex = layout.findIndex((item) => item.i === b);

    const aLayout = aIndex >= 0 ? layout[aIndex] : null;
    const bLayout = bIndex >= 0 ? layout[bIndex] : null;

    const ay = Number.isFinite(aLayout?.y) ? aLayout.y : Number.MAX_SAFE_INTEGER;
    const by = Number.isFinite(bLayout?.y) ? bLayout.y : Number.MAX_SAFE_INTEGER;

    if (ay !== by) return ay - by;

    const ax = Number.isFinite(aLayout?.x) ? aLayout.x : Number.MAX_SAFE_INTEGER;
    const bx = Number.isFinite(bLayout?.x) ? bLayout.x : Number.MAX_SAFE_INTEGER;

    if (ax !== bx) return ax - bx;

    return aIndex - bIndex;
  });

  const getMobileWidgetHeight = (widgetId) => {
    const item = layout.find((layoutItem) => layoutItem.i === widgetId);

    const h = Number.isFinite(item?.h)
      ? Math.max(1, item.h)
      : 4;

    return Math.max(
      280,
      h * rowHeight + Math.max(0, h - 1) * 24
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingLeft: isMobile ? 12 : containerPadding,
        paddingRight: isMobile ? 12 : containerPadding,
        paddingTop: isMobile ? 4 : 16,
        paddingBottom: isMobile ? 16 : 24,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Contact Manager
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? "44px" : undefined,
              whiteSpace: "nowrap",
              borderRadius: "6px",
              fontWeight: "bold",
            }}
          >
            Add Widget
          </button>
        </div>

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              minWidth: 0,
              paddingBottom: "8px",
            }}
          >
            {mobileWidgetEntries.map(([key, WidgetComp]) => (
              <div
                key={key}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobileWidgetHeight(key)}px`,
                  overflow: "hidden",
                }}
              >
                <WidgetWrapper title={getWidgetTitle(key)} onRemove={() => handleRemoveWidget(key)}>
                  {React.isValidElement(WidgetComp) ? WidgetComp : (
                    <div className="text-red-500">Invalid Widget</div>
                  )}
                </WidgetWrapper>
              </div>
            ))}
          </div>
        ) : (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            draggableHandle=".widget-drag-handle"
            resizeHandles={["se"]}
            onLayoutChange={onLayoutChange}
            compactType={null}
            preventCollision={false}
            margin={[16, 24]}
          >
            {Object.entries(widgets).map(([key, WidgetComp]) => (
              <div key={key}>
                <WidgetWrapper title={getWidgetTitle(key)} onRemove={() => handleRemoveWidget(key)}>
                  {React.isValidElement(WidgetComp) ? WidgetComp : (
                    <div className="text-red-500">Invalid Widget</div>
                  )}
                </WidgetWrapper>
              </div>
            ))}
          </GridLayout>
        )}

        {showAddModal && (
          <AddWidgetModal onClose={() => setShowAddModal(false)} onSelect={handleAddWidget} />
        )}

        {selectedUserId && (
          <ManageUserModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
        )}
        {editingCustomer && (
          <EditCustomerModal customer={editingCustomer} onClose={() => setEditingCustomer(null)} />
        )}
        {editingContact && (
          <EditContactModal contact={editingContact} onClose={() => setEditingContact(null)} />
        )}

        {/* ✉️ Contact-scoped messaging modals */}
        {emailModalCfg && (
          <EmailMessageModal
            open={true}
            onClose={() => setEmailModalCfg(null)}
            contactId={emailModalCfg.contactId}
            conversationId={emailModalCfg.conversationId}
            messageId={emailModalCfg.messageId}
            mode={emailModalCfg.mode}
            source={emailModalCfg.source}
          />
        )}

        {smsModalCfg && (
          <SmsMessageModal
            open={true}
            onClose={() => setSmsModalCfg(null)}
            contactId={smsModalCfg.contactId}
            conversationId={smsModalCfg.conversationId}
            messageId={smsModalCfg.messageId}
            mode={smsModalCfg.mode}
            source={smsModalCfg.source}
          />
        )}

        {whatsAppModalCfg && (
          <WhatsAppMessageModal
            open={true}
            onClose={() => setWhatsAppModalCfg(null)}
            contactId={whatsAppModalCfg.contactId}
            conversationId={whatsAppModalCfg.conversationId}
            messageId={whatsAppModalCfg.messageId}
            mode={whatsAppModalCfg.mode}
            source={whatsAppModalCfg.source}
          />
        )}

        {whatsAppTemplateCfg && (
          <WhatsAppTemplateSelectorModal
            open={true}
            onClose={() => setWhatsAppTemplateCfg(null)}
            contactId={whatsAppTemplateCfg.contactId}
            contactName={whatsAppTemplateCfg.contactName || ""}
            contactPhone={whatsAppTemplateCfg.contactPhone || ""}
            jobId={null}
            jobRef=""
            onConfirm={async (payload) => {
              await sendWhatsAppTemplateMessage(payload);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ContactsPage;
